import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { TESTIDS } from "@/constants/testIds";
import { t } from "@/lib/i18n";

const AuthPage = ({ mode }) => {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [userType, setUserType] = useState("home");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = isLogin
      ? await login(email, password)
      : await register(name, email, password, userType);
    setLoading(false);
    if (res.ok) {
      const dest = location.state?.from || "/dashboard";
      navigate(dest, { replace: true });
    } else {
      setError(res.error || "Unable to continue.");
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-1 lg:grid-cols-2">
      {/* Left: visual quote panel */}
      <aside className="hidden lg:flex flex-col justify-between p-12 border-r border-[#E8ECEA]">
        <Link to="/" className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[#2F524A]" />
          <span className="font-serif text-xl">{t("Radon Action & Tracking")}</span>
        </Link>
        <div className="fade-up">
          <p className="eyebrow">{t("Why this matters")}</p>
          <h2 className="font-serif text-4xl mt-4 leading-tight text-[#1C201F]">
            {t("1 in 15 U.S. homes")}
            <span className="block italic text-[#2F524A]">{t("has elevated radon.")}</span>
          </h2>
          <p className="text-[#5C6661] mt-5 max-w-md leading-relaxed">
            {t("Radon is invisible, odorless, and the second-leading cause of lung cancer in the United States. Your first assessment takes about a minute.")}
          </p>
        </div>
        <div className="text-xs text-[#5C6661]">{t("— EPA, \"Citizen’s Guide to Radon\"")}</div>
      </aside>

      {/* Right: form */}
      <section className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md fade-up">
          <p className="eyebrow">{isLogin ? t("Welcome back") : t("Create an account")}</p>
          <h1 className="font-serif text-4xl mt-2 text-[#1C201F] leading-tight">
            {isLogin ? t("Log in to your dashboard.") : t("Start your assessment.")}
          </h1>

          <form onSubmit={onSubmit} className="mt-10 space-y-4" autoComplete="on">
            {!isLogin && (
              <div>
                <label className="eyebrow block mb-2" htmlFor="name">
                  {t("Full name")}
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-field"
                  placeholder={t("Jane Doe")}
                  data-testid={TESTIDS.authNameInput}
                />
              </div>
            )}
            {!isLogin && (
              <div>
                <label className="eyebrow block mb-2">{t("I’m tracking radon for…")}</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="signup-usertype">
                  {[
                    { v: "home", label: t("My home") },
                    { v: "school", label: t("A school") },
                    { v: "library", label: t("A library monitor") },
                    { v: "workplace", label: t("Workplace") },
                    { v: "assistance", label: t("Mitigation assistance") },
                    { v: "community", label: t("Community program") },
                  ].map((o) => (
                    <button
                      type="button"
                      key={o.v}
                      onClick={() => setUserType(o.v)}
                      className={`text-left p-3 rounded-xl border text-sm transition-colors ${
                        userType === o.v
                          ? "border-[#2F524A] bg-[#F4F7F6] text-[#1C201F]"
                          : "border-[#E8ECEA] text-[#5C6661] hover:border-[#2F524A]"
                      }`}
                      data-testid={`usertype-${o.v}`}
                      data-selected={userType === o.v}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="eyebrow block mb-2" htmlFor="email">
                {t("Email")}
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-field"
                placeholder={t("you@example.com")}
                autoComplete="email"
                data-testid={TESTIDS.authEmailInput}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="eyebrow block" htmlFor="password">
                  {t("Password")}
                </label>
                {isLogin && (
                  <Link to="/forgot-password" className="text-xs text-[#2F524A] font-semibold hover:underline"
                    data-testid="auth-forgot-password-link">
                    {t("Forgot password?")}
                  </Link>
                )}
              </div>
              <input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                placeholder={t("At least 6 characters")}
                autoComplete={isLogin ? "current-password" : "new-password"}
                data-testid={TESTIDS.authPasswordInput}
              />
            </div>

            {error && (
              <div
                data-testid={TESTIDS.authError}
                className="text-sm text-[#9B2C2C] bg-[#FCEDED] border border-[#F5C2C2] rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full !rounded-xl"
              data-testid={TESTIDS.authSubmit}
            >
              {loading ? t("Working…") : isLogin ? t("Log in") : t("Create account")}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#5C6661]">
            {isLogin ? t("Don\u2019t have an account?") : t("Already have an account?")}{" "}
            <Link
              to={isLogin ? "/signup" : "/login"}
              data-testid={TESTIDS.authSwitch}
              className="text-[#2F524A] font-semibold hover:underline"
            >
              {isLogin ? t("Sign up") : t("Log in")}
            </Link>
          </p>

        </div>
      </section>
    </div>
  );
};

export default AuthPage;
